import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { cloneDeep, last } from 'lodash';

import type {
  ActivityIngredient,
  RecipeIngredient,
  RecipeMainIngredient,
  StayIngredient,
  TransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  FerryIngredient,
  FlightIngredient,
  HotelIngredient,
  OwnTransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import {
  isFerryIngredient,
  isFlightIngredient,
  isHotelIngredient,
  isOwnStayIngredient,
  isOwnTransportIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import { FlightEndpoint } from '@apps/main/modules/api/flight';
import type { UpdateIngredientByIdData } from '@apps/main/modules/recipe/ingredients/updateIngredient';
import { UpdateIngredientDataType } from '@apps/main/modules/recipe/ingredients/updateIngredient';
import { getOwnTransportTypeFromConnectionType } from '@apps/main/modules/router/serializer/ingredientSerializer/legacy/ownTransportSerializer/deserialize';

import {
  assertValue,
  exhaustiveGuard,
  getConnectionTypeFromOwnTransportType,
  isDefined,
  isFerryTransport,
  isFlightTransport,
  readonlyRef,
} from '@libs/utils';
import type {
  ActivityTicket,
  AutoCompleteSuggestionsByCategory,
  AvailabilityOrder,
  IngredientId,
  SearchTerm,
  RecipeMetaData,
  TransportDirection,
  ConnectionType,
  OwnTransportType,
  HeartedHotel,
} from '@libs/trip-planner-interfaces';
import { SearchTermType, TransportType } from '@libs/trip-planner-interfaces';
import { trackDestinationRemoved } from '@libs/google-analytics-events';
import type { MonthIndex } from '@libs/sembo-dates';
import { SemboDate } from '@libs/sembo-dates';

import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { usePreferredTimeStore } from '../preferredTimeStore';

export interface RecipeStoreProps {
  recipe?: RecipeMainIngredient[];
  preferredDay?: Date;
  preferredMonth?: MonthIndex;
  itinerarySuggestionSortType?: AvailabilityOrder;
  returnTransport?: ConnectionType;
  metadata?: RecipeMetaData;
  searchForPackageRates?: boolean | null;
  isCarTrip: boolean;
}

export const useRecipeStore = defineStore('RecipeStore', () => {
  const editingIngredientStore = useEditingIngredientStore();
  const preferredTimeStore = usePreferredTimeStore();

  // State
  const recipe = ref<RecipeMainIngredient[]>([]);
  const itinerarySuggestionSortType = ref<AvailabilityOrder>();
  const selectedActivityTickets = ref<ActivityTicket[]>([]);
  const endLocationIngredient = ref<HotelIngredient>();
  const settingTransportIngredient = ref<RecipeMainIngredient | null>(null);
  const shouldSkipItinerarySearch = ref<boolean>(false);
  const searchActivities = ref<boolean>(false);
  const metaData = ref<RecipeMetaData | undefined>();
  const searchForPackageRates = ref<boolean | undefined | null>();
  const returnTransport = ref<ConnectionType | undefined>();
  const searchSuggestionResults = ref<AutoCompleteSuggestionsByCategory[]>([]);

  const isSingleHotelRecipe = computed<boolean>(() => {
    const threeIngredients = 3;
    if (recipe.value.length !== threeIngredients) {
      return false;
    }
    const first = recipe.value[0];
    const second = recipe.value[1];
    const third = recipe.value[2];

    assertValue(first, 'recipeStore > isHotelOnlyRecipe > first');
    assertValue(second, 'recipeStore > isHotelOnlyRecipe > second');
    assertValue(third, 'recipeStore > isHotelOnlyRecipe > third');

    const isFirstTransportNone =
      isOwnTransportIngredient(first) &&
      first.ownTransportType === TransportType.None;
    const isSecondTransportNone =
      isOwnTransportIngredient(third) &&
      third.ownTransportType === TransportType.None;

    return (
      isFirstTransportNone && isStayIngredient(second) && isSecondTransportNone
    );
  });

  const ferryOnlyRecipe = computed<FerryIngredient[]>(() =>
    recipe.value.filter(isFerryIngredient)
  );

  const hotelOnlyRecipe = computed<HotelIngredient[]>(() =>
    recipe.value.filter(isHotelIngredient)
  );

  const stayOnlyRecipe = computed<(HotelIngredient | OwnStayIngredient)[]>(() =>
    recipe.value.filter(isStayIngredient)
  );

  const flightOnlyRecipe = computed<FlightIngredient[]>(() =>
    recipe.value.filter(isFlightIngredient)
  );

  const transportOnlyRecipe = computed<TransportIngredient[]>(() =>
    recipe.value.filter(isTransportIngredient)
  );

  const recipeWithoutOwnTransport = computed<RecipeMainIngredient[]>(() =>
    recipe.value.filter((ingredient) => !isOwnTransportIngredient(ingredient))
  );

  const doesAnyStayHaveSubingredients = computed<boolean>(() =>
    stayOnlyRecipe.value.some((ingredient) => ingredient.subIngredients.length)
  );

  const totalNights = computed<number>(() =>
    stayOnlyRecipe.value.reduce(
      (accumulator: number, ingredient: StayIngredient) =>
        accumulator + (ingredient.numberOfNights ?? 0),
      0
    )
  );

  const isRecipeCorrect = computed(() =>
    recipe.value.every((ingredient: RecipeMainIngredient) =>
      ingredient.isIngredientCorrect()
    )
  );

  const activityOnlyRecipe = computed<ActivityIngredient[]>(() =>
    recipe.value.reduce(
      (
        activityIngredients: ActivityIngredient[],
        currentIngredient: RecipeMainIngredient
      ) => {
        if (
          isStayIngredient(currentIngredient) &&
          currentIngredient.subIngredients.length
        ) {
          return [...activityIngredients, ...currentIngredient.subIngredients];
        }
        return activityIngredients;
      },
      []
    )
  );

  const hasRecipeIngredients = computed<boolean>(() => recipe.value.length > 0);

  const amountOfIngredientsInRecipe = computed<number>(() =>
    recipe.value.reduce(
      (accumulator: number, ingredient: RecipeMainIngredient) => {
        if (isStayIngredient(ingredient)) {
          return accumulator + ingredient.subIngredients.length + 1;
        }
        return accumulator + 1;
      },
      0
    )
  );

  const ownStayOnlyRecipe = computed<OwnStayIngredient[]>(() =>
    recipe.value.filter(isOwnStayIngredient)
  );

  const isSettingFirstTransport = computed<boolean>(() => {
    const firstStayIngredient = stayOnlyRecipe.value[0];
    assertValue(
      firstStayIngredient,
      'recipeStore.ts > isSettingFirstTransport = computed > firstStayIngredient'
    );

    return (
      !!settingTransportIngredient.value &&
      settingTransportIngredient.value.ingredientId ===
        firstStayIngredient.ingredientId
    );
  });

  const transportsByIngredient = computed<Record<string, ConnectionType>>(() =>
    recipe.value.reduce<Record<string, ConnectionType>>(
      (acc, component, index, recipe) => {
        if (isStayIngredient(component)) {
          const previousIngredient = recipe[index - 1];
          if (
            previousIngredient &&
            isOwnTransportIngredient(previousIngredient)
          ) {
            acc[component.ingredientId] = getConnectionTypeFromOwnTransportType(
              previousIngredient.ownTransportType
            );
            return acc;
          }
        }
        if (isTransportIngredient(component)) {
          acc[component.ingredientId] = component.transportTypeToBeReached;

          return acc;
        }
        return acc;
      },
      {}
    )
  );

  const startingTransportExists = computed<boolean>(() => {
    const firstIngredient = recipe.value[0];

    return (
      !!firstIngredient &&
      ((isOwnTransportIngredient(firstIngredient) &&
        firstIngredient.ownTransportType !== TransportType.None) ||
        isTransportIngredient(firstIngredient))
    );
  });

  const endingTransportExists = computed<boolean>(() => {
    const lastIngredient = last(recipe.value);

    return (
      !!stayOnlyRecipe.value[0] &&
      !!lastIngredient &&
      (isTransportIngredient(lastIngredient) ||
        (isOwnTransportIngredient(lastIngredient) &&
          lastIngredient.ownTransportType !== TransportType.None))
    );
  });

  const anyRoomSelected = computed<boolean>(() =>
    hotelOnlyRecipe.value.some((hotelIngredient) =>
      hotelIngredient.heartedHotels.some(
        (heartedHotel) =>
          !!heartedHotel.rooms?.length || !!heartedHotel.roomCodes?.length
      )
    )
  );

  // Actions

  function initializeProps(props: RecipeStoreProps): void {
    returnTransport.value = props.returnTransport;

    metaData.value = props.metadata;
    itinerarySuggestionSortType.value = props.itinerarySuggestionSortType;
    preferredTimeStore.setInitialValueForDayAndMonth(
      props.preferredDay,
      props.preferredMonth
    );
    searchForPackageRates.value = props.searchForPackageRates;
    if (props.recipe) {
      recipe.value = getInitialRecipe(props.recipe, props.isCarTrip);
    }
    if (recipe.value.length) {
      setReturnLocation(
        props.isCarTrip ? TransportType.Car : TransportType.None
      );
    }
    const isRecipeCorrect = checkIsRecipeCorrect(recipe.value);
    if (!isRecipeCorrect) {
      recipe.value = adjustRecipe(recipe.value, props.isCarTrip);
    }
  }

  function checkIsRecipeCorrect(recipe: RecipeMainIngredient[]): boolean {
    if (recipe.length % 2 === 0) {
      return false;
    }

    return recipe.every((ingredient, index) => {
      if (index % 2 === 0) {
        return (
          isOwnTransportIngredient(ingredient) ||
          isTransportIngredient(ingredient)
        );
      } else {
        return isStayIngredient(ingredient);
      }
    });
  }

  function adjustRecipe(
    recipe: RecipeMainIngredient[],
    isCarTripRoute: boolean
  ): RecipeMainIngredient[] {
    const newRecipe: RecipeMainIngredient[] = [];

    recipe.forEach((ingredient, index) => {
      if (isStayIngredient(ingredient)) {
        const previousIngredient = recipe[index - 1];

        if (!previousIngredient || isStayIngredient(previousIngredient)) {
          const ownTransportType = isCarTripRoute
            ? TransportType.Car
            : getOwnTransportTypeFromConnectionType(
                ingredient.transportTypeToBeReached
              );
          const ownTransportIngredient = new OwnTransportIngredient(
            ownTransportType
          );
          newRecipe.push(ownTransportIngredient);
        }
      }

      newRecipe.push(ingredient);
    });

    const lastIngredient = recipe[recipe.length - 1];
    if (lastIngredient && isStayIngredient(lastIngredient)) {
      const ownTransportType = isCarTripRoute
        ? TransportType.Car
        : getOwnTransportTypeFromConnectionType(
            lastIngredient.transportTypeToBeReached
          );
      const ownTransportIngredient = new OwnTransportIngredient(
        ownTransportType
      );
      newRecipe.push(ownTransportIngredient);
    }

    return newRecipe;
  }

  function setSearchSuggestionResults(
    response: AutoCompleteSuggestionsByCategory[]
  ): void {
    searchSuggestionResults.value = response;
  }

  function updateRecipe(newRecipe: RecipeMainIngredient[]): void {
    recipe.value = newRecipe;
  }

  function getStayIngredient(
    ingredientId: IngredientId
  ): OwnStayIngredient | HotelIngredient {
    const foundIngredient = recipe.value.find(
      (ingredient) => ingredient.ingredientId === ingredientId
    );

    if (!foundIngredient || !isStayIngredient(foundIngredient)) {
      throw new Error('StayIngredient not found');
    }

    return foundIngredient;
  }

  function updateHotelIngredientById(
    ingredientId: IngredientId,
    searchTerm: SearchTerm
  ): void {
    return updateIngredientById({
      updateType: UpdateIngredientDataType.StayIngredientSearchText,
      searchTerm,
      ingredientId,
    });
  }

  function updateHotelHeartedHotel(
    ingredientId: IngredientId,
    heartedHotel: HeartedHotel
  ): void {
    return updateIngredientById({
      updateType: UpdateIngredientDataType.SetHeartedHotel,
      heartedHotel,
      ingredientId,
    });
  }

  function updateIngredientById(data: UpdateIngredientByIdData): void {
    const updateType = data.updateType;

    switch (updateType) {
      case UpdateIngredientDataType.StayIngredientSearchText: {
        const stayIngredient = getStayIngredient(data.ingredientId);

        if (isOwnStayIngredient(stayIngredient)) {
          const newHotelIngredient = new HotelIngredient(data.searchTerm);
          newHotelIngredient.numberOfNights = stayIngredient.numberOfNights;

          replaceIngredient(stayIngredient, newHotelIngredient);
        }

        if (isHotelIngredient(stayIngredient)) {
          stayIngredient.setSearchTerm(data.searchTerm);
          stayIngredient.heartedHotels = [];
          stayIngredient.subIngredients = [];
          stayIngredient.polygonConstraint = undefined;
          stayIngredient.nearbyPolygon = undefined;
          stayIngredient.commonPolygon = undefined;
        }
        break;
      }

      case UpdateIngredientDataType.SetHeartedHotel: {
        const hotelIngredient = getHotelIngredient(data.ingredientId);
        hotelIngredient.heartedHotels = [data.heartedHotel];
        break;
      }

      default:
        exhaustiveGuard(updateType, 'Unhandled type in updateIgredientById');
    }
  }

  function addCarTripIngredient(ingredient: RecipeMainIngredient): void {
    if (!isStayIngredient(ingredient)) {
      return;
    }

    const ownTransport = new OwnTransportIngredient(TransportType.Car);
    const secondOwnTransport = new OwnTransportIngredient(TransportType.Car);
    recipe.value.push(ownTransport);
    recipe.value.push(ingredient);
    recipe.value.push(secondOwnTransport);

    if (hotelOnlyRecipe.value.length === 1) {
      setReturnLocation(TransportType.Car);
    }
  }

  function addIngredient(
    ingredient: RecipeMainIngredient,
    index?: number
  ): void {
    if (isStayIngredient(ingredient)) {
      const lastIngredient = last(recipe.value);
      const hasReturnFlight =
        lastIngredient && isFlightIngredient(lastIngredient);

      let returnOwnTransportIngredient: OwnTransportIngredient | undefined;
      if (lastIngredient && isOwnTransportIngredient(lastIngredient)) {
        returnOwnTransportIngredient =
          recipe.value.pop() as OwnTransportIngredient;
      }
      const previousIngredient = recipe.value.at(-1);
      if (
        !previousIngredient ||
        (previousIngredient && isStayIngredient(previousIngredient)) ||
        (previousIngredient && hasReturnFlight)
      ) {
        const ownTransportIngredient = new OwnTransportIngredient(
          TransportType.None
        );
        recipe.value.push(ownTransportIngredient);
      }

      recipe.value.push(ingredient);

      if (returnOwnTransportIngredient) {
        recipe.value.push(returnOwnTransportIngredient);
      }

      if (hasReturnFlight && isStayIngredient(ingredient)) {
        updateSuggestedReturnFlight(ingredient.getSearchText(), lastIngredient);
      }

      if (
        recipe.value.length &&
        !returnOwnTransportIngredient &&
        !hasReturnFlight
      ) {
        setReturnLocation(TransportType.None);
      }

      return;
    }

    const thereIsIndex = typeof index === 'number' && index >= 0;

    if (thereIsIndex) {
      recipe.value.splice(index, 0, ingredient);
    } else {
      recipe.value.push(ingredient);
    }
  }

  function setReturnLocation(transportType: OwnTransportType): void {
    if (!endLocationIngredient.value) {
      endLocationIngredient.value = new HotelIngredient(
        { type: SearchTermType.FreeText, freeText: 'End Location' },
        true
      );
    }
    const previousIngredient = recipe.value.at(-1);
    if (previousIngredient && isStayIngredient(previousIngredient)) {
      const ownTransportIngredient = new OwnTransportIngredient(transportType);
      addIngredient(ownTransportIngredient);
    }

    const lastIngredient = recipe.value.at(-1);
    const firstIngredient = recipe.value[0];
    assertValue(
      firstIngredient,
      'recipeStore.ts > setReturnLocation > firstIngredient'
    );
    if (
      lastIngredient &&
      isOwnTransportIngredient(lastIngredient) &&
      returnTransport.value
    ) {
      lastIngredient.ownTransportType = getOwnTransportTypeFromConnectionType(
        returnTransport.value
      );
    }
  }

  function updateSuggestedReturnFlight(
    newFromPortSearchText: string,
    previousReturnFlight: FlightIngredient
  ): void {
    const endpoint = new FlightEndpoint();

    deleteIngredient(previousReturnFlight);

    endpoint
      .getSuggestedAirport(newFromPortSearchText)
      .subscribe((airportStaticInfo) => {
        if (airportStaticInfo) {
          const newReturnFlight = new FlightIngredient();
          newReturnFlight.fromPort = airportStaticInfo.code;
          newReturnFlight.fromPortText = airportStaticInfo.city;
          newReturnFlight.fromPortDisplayText = airportStaticInfo.label;

          newReturnFlight.toPort = previousReturnFlight.toPort;
          newReturnFlight.toPortText = previousReturnFlight.toPortText;
          newReturnFlight.toPortDisplayText =
            previousReturnFlight.toPortDisplayText;

          addIngredient(newReturnFlight);
        } else {
          const ownTransportIngredient = new OwnTransportIngredient(
            TransportType.None
          );
          addIngredient(ownTransportIngredient);
        }
      });
  }

  function deleteIngredientById(ingredientId: IngredientId): void {
    const ingredient = getIngredient(ingredientId);

    deleteAssociatedTransport(ingredientId);

    deleteIngredient(ingredient);
  }

  function deleteAssociatedTransport(ingredientId: IngredientId): void {
    const ingredient = getIngredient(ingredientId);
    if (!isStayIngredient(ingredient)) {
      return;
    }

    const previousIngredient = getPreviousIngredient(ingredientId);
    if (previousIngredient && isOwnTransportIngredient(previousIngredient)) {
      deleteIngredient(getIngredient(previousIngredient.ingredientId));
    }

    const nextIngredient = getNextIngredient(ingredientId);
    if (nextIngredient && isOwnTransportIngredient(nextIngredient)) {
      deleteIngredient(getIngredient(nextIngredient.ingredientId));
    }
  }

  function getFerryIngredient(
    ferryIngredientId: IngredientId
  ): FerryIngredient {
    const foundIngredient = ferryOnlyRecipe.value.find(
      (ferryIngredient) => ferryIngredient.ingredientId === ferryIngredientId
    );

    if (!foundIngredient) {
      throw new Error('FerryIngredient not found');
    }

    return foundIngredient;
  }

  function getHotelIngredient(
    hotelIngredientId: IngredientId
  ): HotelIngredient {
    const foundIngredient = hotelOnlyRecipe.value.find(
      (hotelIngredient) => hotelIngredient.ingredientId === hotelIngredientId
    );

    if (!foundIngredient) {
      throw new Error('HotelIngredient not found');
    }

    return foundIngredient;
  }

  function getFlightIngredient(
    flightIngredientId: IngredientId
  ): FlightIngredient {
    const foundIngredient = flightOnlyRecipe.value.find(
      (flightIngredient) => flightIngredient.ingredientId === flightIngredientId
    );

    if (!foundIngredient) {
      throw new Error('FlightIngredient not found');
    }

    return foundIngredient;
  }

  function removeAllSelectedRooms(): void {
    hotelOnlyRecipe.value.forEach((hotel) =>
      hotel.heartedHotels.forEach((heartedHotel) => {
        heartedHotel.rooms = [];
        heartedHotel.roomCodes = [];
      })
    );
  }

  function updateNumberOfFerryVehiclesBaseOnAdults(
    numberOfAdults: number
  ): void {
    ferryOnlyRecipe.value.forEach((ferry) => {
      if (ferry.vehicles.length > numberOfAdults) {
        const clonedVehicles = cloneDeep(ferry.vehicles);
        clonedVehicles.length = numberOfAdults;
        ferry.vehicles = clonedVehicles;
      }
    });
  }

  function getSelectedTicketsForRate(
    rateId: string,
    dateOffset: number,
    isAlternative?: boolean
  ): ActivityTicket[] {
    return isAlternative
      ? []
      : selectedActivityTickets.value.filter(
          (selectedTicket) =>
            selectedTicket.rateId === rateId &&
            selectedTicket.dateOffset === dateOffset
        );
  }

  function updateSelectedTickets(
    updatedTickets: ActivityTicket[],
    rateId: string,
    dateOffset: number
  ): void {
    selectedActivityTickets.value = selectedActivityTickets.value.filter(
      (selectedTicket) =>
        !(
          selectedTicket.dateOffset === dateOffset &&
          selectedTicket.rateId === rateId
        )
    );

    selectedActivityTickets.value =
      selectedActivityTickets.value.concat(updatedTickets);
  }

  function updateActivityTickets(value: ActivityTicket[]): void {
    selectedActivityTickets.value = value;
  }

  function getSubIngredientsByCode(
    parentIngredientId: IngredientId,
    subIngredientCode: string
  ): ActivityIngredient[] | undefined {
    const ingredient = getStayIngredient(parentIngredientId);

    return ingredient.subIngredients.filter(
      (subIngredient: ActivityIngredient) =>
        subIngredient.code === subIngredientCode
    );
  }

  function updateSearchActivities(value: boolean): void {
    searchActivities.value = value;
  }

  function getIngredient(id: string): RecipeIngredient {
    const ingredients = recipe.value;
    const subIngredients = stayOnlyRecipe.value.flatMap(
      (i) => i.subIngredients
    );
    const foundIngredient = [...ingredients, ...subIngredients].find(
      (ingredient) => ingredient.ingredientId === id
    );

    if (!foundIngredient) {
      throw new Error(`Ingredient with ID:${id} not found`);
    }

    return foundIngredient;
  }

  function getIngredientIndex(id: string): number {
    return recipe.value.findIndex(
      (ingredient) => ingredient.ingredientId === id
    );
  }

  function deleteDestinationIngredient(ingredientId: IngredientId): void {
    const ingredient = getIngredient(ingredientId);

    if (ingredient && !ingredient.isNewIngredient) {
      if (isHotelIngredient(ingredient)) {
        trackDestinationRemoved();
        updateConsecutiveFlightIngredients(ingredient);
      }
      const previousIngredient = getPreviousIngredient(ingredientId);
      if (
        isStayIngredient(ingredient) &&
        previousIngredient &&
        isOwnTransportIngredient(previousIngredient)
      ) {
        const ownTransportIngredient = getIngredient(
          previousIngredient.ingredientId
        );
        deleteIngredient(ownTransportIngredient);
      }
      deleteIngredient(ingredient);
    }
  }

  //TODO Check how to improve this function or remove it after the flex transport selection.
  //More if than needed to avoid creating a new flight and to pushing it into the correct recipe place.
  function updateConsecutiveFlightIngredients(
    ingredient: HotelIngredient
  ): void {
    const indexOfIngredient = recipe.value.indexOf(ingredient);
    const previousIngredient = recipe.value[indexOfIngredient - 1] ?? null;
    const nextIngredient = recipe.value[indexOfIngredient + 1] ?? null;

    const previousIngredientIsFlgiht =
      previousIngredient && isFlightIngredient(previousIngredient);
    const nextIngredientIsFlgiht =
      nextIngredient && isFlightIngredient(nextIngredient);
    const connectedIngredientsAreFlights =
      previousIngredientIsFlgiht && nextIngredientIsFlgiht;

    if (
      connectedIngredientsAreFlights &&
      !areTransportsMirrored(previousIngredient, nextIngredient)
    ) {
      nextIngredient.fromPort = previousIngredient.fromPort;
      nextIngredient.fromPortDisplayText =
        previousIngredient.fromPortDisplayText;
      nextIngredient.fromPortText = previousIngredient.fromPortText;
      deleteIngredient(previousIngredient);
      return;
    }

    if (previousIngredient && isTransportIngredient(previousIngredient)) {
      deleteIngredient(previousIngredient);
    }

    if (nextIngredient && isFlightIngredient(nextIngredient)) {
      const indexOfStayIngredient = stayOnlyRecipe.value.indexOf(ingredient);

      const isLastStayIngredient =
        indexOfStayIngredient === stayOnlyRecipe.value.length - 1;

      if (!isLastStayIngredient) {
        // TODO: update next flight with suggested instead of removing
        deleteIngredient(nextIngredient);
        return;
      }

      const prevoiusStayIngredient =
        stayOnlyRecipe.value[indexOfStayIngredient - 1];

      if (!prevoiusStayIngredient) {
        return;
      }

      updateSuggestedReturnFlight(
        prevoiusStayIngredient.getSearchText(),
        nextIngredient
      );
    }
  }

  function areTransportsMirrored(
    transport1: TransportIngredient,
    transport2: TransportIngredient
  ): boolean {
    return (
      transport1.fromPort === transport2.toPort &&
      transport1.toPort === transport2.fromPort
    );
  }

  function createTransportIngredient(type: ConnectionType): void {
    if (isFlightTransport(type)) {
      createFlightIngredient();
    } else if (isFerryTransport(type)) {
      createFerryIngredient();
    } else {
      throw Error(`Cannot create new Transport ingredient from type: ${type}`);
    }
  }

  function createFlightIngredient(): void {
    editingIngredientStore.flightIngredientFrom = 'recipe';
    editingIngredientStore.editingFlightIngredient = new FlightIngredient();
    editingIngredientStore.editingFlightIngredient.isNewIngredient = true;
  }

  function createFerryIngredient(): void {
    editingIngredientStore.ferryIngredientFrom = 'recipe';
    editingIngredientStore.editingFerryIngredient = new FerryIngredient();
    editingIngredientStore.editingFerryIngredient.isNewIngredient = true;
  }

  function deleteIngredient(ingredient: RecipeIngredient): void {
    const numberOfStayIngredients = stayOnlyRecipe.value.length;

    const shouldClearRecipe =
      isStayIngredient(ingredient) && numberOfStayIngredients === 1;

    if (shouldClearRecipe) {
      recipe.value = [];
      return;
    }

    if (isHotelIngredient(ingredient)) {
      updateConsecutiveFlightIngredients(ingredient);
    }
    if (
      isTransportIngredient(ingredient) ||
      isStayIngredient(ingredient) ||
      isOwnTransportIngredient(ingredient)
    ) {
      recipe.value.splice(recipe.value.indexOf(ingredient), 1);
    } else {
      deleteSubIngredient(
        ingredient.code,
        ingredient.parentIngredientId,
        ingredient.ingredientId
      );
    }
  }

  function deleteSubIngredient(
    subIngredientCode: string,
    parentIngredientId: IngredientId,
    ingredientId: string
  ): void {
    const parentIngredient = getStayIngredient(parentIngredientId);

    parentIngredient.subIngredients = parentIngredient.subIngredients.filter(
      (subIngredient) =>
        !(
          subIngredient.code === subIngredientCode &&
          subIngredient.ingredientId === ingredientId
        )
    );
  }

  function getPreviousIngredient(
    currentIngredientId: IngredientId
  ): RecipeIngredient | undefined {
    const index = recipe.value.findIndex(
      (ingredient) => ingredient.ingredientId === currentIngredientId
    );
    return index > 0 ? recipe.value[index - 1] : undefined;
  }

  function getNextIngredient(
    ingredientId: IngredientId
  ): RecipeMainIngredient | undefined {
    const index = recipe.value.findIndex(
      (ingredient) => ingredient.ingredientId === ingredientId
    );
    return recipe.value[index + 1];
  }

  function replaceIngredient(
    oldIngredient: RecipeMainIngredient,
    newIngredient: RecipeMainIngredient
  ): void {
    const oldIngredientIndex = getIngredientIndex(oldIngredient.ingredientId);

    recipe.value.splice(oldIngredientIndex, 1, newIngredient);
  }

  function getOwnStayIngredient(ingredientId: string): OwnStayIngredient {
    const foundIngredient = ownStayOnlyRecipe.value.find(
      (ownStayIngredient) => ownStayIngredient.ingredientId === ingredientId
    );

    if (!foundIngredient) {
      throw new Error('OwnStayIngredient not found');
    }

    return foundIngredient;
  }

  function getActivityIngredient(
    activityIngredientId: IngredientId
  ): ActivityIngredient {
    const subIngredients = stayOnlyRecipe.value.flatMap(
      (stayIngredient) => stayIngredient.subIngredients
    );

    const foundIngredient = subIngredients.find(
      (subIngredient) => subIngredient.ingredientId === activityIngredientId
    );

    if (!foundIngredient) {
      throw new Error('ActivityIngredient not found');
    }

    return foundIngredient;
  }

  function editIngredientTransport(ingredient: RecipeMainIngredient): void {
    settingTransportIngredient.value = ingredient;
  }

  function getMirroredTransport(
    transport: FlightIngredient | FerryIngredient
  ): FlightIngredient | FerryIngredient {
    const returnFlightFerryTransport = isFlightIngredient(transport)
      ? new FlightIngredient()
      : new FerryIngredient();
    updatePortsFromOtherTransport(
      returnFlightFerryTransport,
      'from',
      transport,
      'to'
    );
    updatePortsFromOtherTransport(
      returnFlightFerryTransport,
      'to',
      transport,
      'from'
    );

    if (
      isFerryIngredient(transport) &&
      isFerryIngredient(returnFlightFerryTransport)
    ) {
      returnFlightFerryTransport.vehicles = transport.vehicles;
      returnFlightFerryTransport.allowMultipleVehicles =
        transport.allowMultipleVehicles;
    }

    return returnFlightFerryTransport;
  }

  function updatePortsFromOtherTransport(
    toTransport: TransportIngredient,
    toCheckPoint: TransportDirection,
    fromTransport: TransportIngredient,
    fromCheckPoint: TransportDirection
  ): void {
    if (toCheckPoint === 'from') {
      if (fromCheckPoint === 'from') {
        toTransport.fromPort = fromTransport.fromPort;
        toTransport.fromPortDisplayText = fromTransport.fromPortDisplayText;
        toTransport.fromPortText = fromTransport.fromPortText;
      } else {
        toTransport.fromPort = fromTransport.toPort;
        toTransport.fromPortDisplayText = fromTransport.toPortDisplayText;
        toTransport.fromPortText = fromTransport.toPortText;
      }
    } else {
      if (fromCheckPoint === 'from') {
        toTransport.toPort = fromTransport.fromPort;
        toTransport.toPortDisplayText = fromTransport.fromPortDisplayText;
        toTransport.toPortText = fromTransport.fromPortText;
      } else {
        toTransport.toPort = fromTransport.toPort;
        toTransport.toPortDisplayText = fromTransport.toPortDisplayText;
        toTransport.toPortText = fromTransport.toPortText;
      }
    }
  }

  function updateShouldSkipItinerarySearch(value: boolean): void {
    shouldSkipItinerarySearch.value = value;
  }

  function getTransportIngredient(
    transportIngredientId: IngredientId
  ): FlightIngredient | FerryIngredient {
    const foundIngredient = recipe.value.find(
      (ingredient) => ingredient.ingredientId === transportIngredientId
    );

    if (!foundIngredient || !isTransportIngredient(foundIngredient)) {
      throw new Error('TransportIngredient not found');
    }

    return foundIngredient;
  }

  function getIngredientDateInRecipe(
    ingredientId: IngredientId,
    selectedDate: Date
  ): Date | undefined {
    const ingredientIndex = getIngredientIndex(ingredientId);

    if (ingredientIndex === -1) {
      throw new Error('Can not find ingredient index.');
    }

    let hasMissingNumberOfNights = false;

    const ingredientOffset = recipe.value.reduce(
      (offset: number, ingredient, index) => {
        if (isStayIngredient(ingredient) && index < ingredientIndex) {
          isDefined(ingredient.numberOfNights)
            ? (offset += ingredient.numberOfNights)
            : (hasMissingNumberOfNights = true);
        }

        return offset;
      },
      0
    );

    if (hasMissingNumberOfNights) {
      return;
    }

    return new SemboDate(selectedDate).add({ days: ingredientOffset }).get();
  }

  function getExistingSelectedTickets(
    activityCode: string,
    ingredientId: IngredientId
  ): ActivityTicket[] {
    const ingredient = getStayIngredient(ingredientId);

    const activitySubIngredients = ingredient.subIngredients?.filter(
      (subIngredient) => subIngredient.code === activityCode
    );

    if (!activitySubIngredients?.length) {
      return [];
    }

    return activitySubIngredients.flatMap((subIngredient) =>
      subIngredient.tickets.map((selectedTicket) => ({
        ...selectedTicket,
        code: subIngredient.code,
        dateOffset: subIngredient.offset,
      }))
    );
  }

  function getInitialRecipe(
    ingredients: RecipeMainIngredient[],
    isCarTrip: boolean
  ): RecipeMainIngredient[] {
    return ingredients.map((ingredient, index) => {
      if (isStayIngredient(ingredient) && index >= 1 && isCarTrip) {
        const ownTransport = new OwnTransportIngredient(TransportType.Car);
        addIngredient(ownTransport, 0);
      }

      if (
        !isOwnStayIngredient(ingredient) &&
        !isOwnTransportIngredient(ingredient) &&
        itinerarySuggestionSortType.value
      ) {
        ingredient.availabilityOrder = itinerarySuggestionSortType.value;
      }

      return ingredient;
    });
  }

  function mapRecipeIndexToStayOnlyIndex(
    ingredientIndex: number
  ): number | undefined {
    const ingredientByRecipeIndex = recipe.value[ingredientIndex];

    if (!ingredientByRecipeIndex) {
      return;
    }

    const foundIndex = stayOnlyRecipe.value.findIndex(
      (stayIngredient) =>
        stayIngredient.ingredientId === ingredientByRecipeIndex.ingredientId
    );

    return foundIndex > -1 ? foundIndex : undefined;
  }

  function shouldShowNumberOfVehiclesUpdatedWarning(
    numberOfAdults: number
  ): boolean {
    return ferryOnlyRecipe.value.some(
      (ferry) => ferry.vehicles.length > numberOfAdults
    );
  }

  return {
    // State
    recipe: readonlyRef(recipe),
    ferryOnlyRecipe: readonlyRef(ferryOnlyRecipe),
    hotelOnlyRecipe: readonlyRef(hotelOnlyRecipe),
    stayOnlyRecipe: readonlyRef(stayOnlyRecipe),
    flightOnlyRecipe: readonlyRef(flightOnlyRecipe),
    transportOnlyRecipe: readonlyRef(transportOnlyRecipe),
    recipeWithoutOwnTransport: readonlyRef(recipeWithoutOwnTransport),
    doesAnyStayHaveSubingredients: readonlyRef(doesAnyStayHaveSubingredients),
    isRecipeCorrect: readonlyRef(isRecipeCorrect),
    selectedActivityTickets: readonlyRef(selectedActivityTickets),
    activityOnlyRecipe: readonlyRef(activityOnlyRecipe),
    hasRecipeIngredients: readonlyRef(hasRecipeIngredients),
    endLocationIngredient: readonlyRef(endLocationIngredient),
    settingTransportIngredient: readonlyRef(settingTransportIngredient),
    amountOfIngredientsInRecipe: readonlyRef(amountOfIngredientsInRecipe),
    ownStayOnlyRecipe: readonlyRef(ownStayOnlyRecipe),
    isSettingFirstTransport: readonlyRef(isSettingFirstTransport),
    metaData: readonlyRef(metaData),
    searchForPackageRates: readonlyRef(searchForPackageRates),
    shouldSkipItinerarySearch: readonlyRef(shouldSkipItinerarySearch),
    searchActivities: readonlyRef(searchActivities),
    transportsByIngredient: readonlyRef(transportsByIngredient),
    startingTransportExists: readonlyRef(startingTransportExists),
    endingTransportExists: readonlyRef(endingTransportExists),

    // Actions
    updateActivityTickets,
    setSearchSuggestionResults,
    updateRecipe,
    getStayIngredient,
    updateHotelIngredientById,
    addCarTripIngredient,
    addIngredient,
    deleteIngredientById,
    getFerryIngredient,
    getHotelIngredient,
    getFlightIngredient,
    removeAllSelectedRooms,
    updateNumberOfFerryVehiclesBaseOnAdults,
    getSelectedTicketsForRate,
    updateSelectedTickets,
    getSubIngredientsByCode,
    updateSearchActivities,
    getIngredient,
    getIngredientIndex,
    deleteDestinationIngredient,
    createTransportIngredient,
    deleteIngredient,
    getPreviousIngredient,
    getNextIngredient,
    replaceIngredient,
    getOwnStayIngredient,
    getActivityIngredient,
    editIngredientTransport,
    getMirroredTransport,
    updateShouldSkipItinerarySearch,
    getTransportIngredient,
    getIngredientDateInRecipe,
    getExistingSelectedTickets,
    initializeProps,
    updateHotelHeartedHotel,

    // Getters
    mapRecipeIndexToStayOnlyIndex,
    anyRoomSelected,
    shouldShowNumberOfVehiclesUpdatedWarning,
    isSingleHotelRecipe,
    totalNights,
  };
});
